/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React, { useEffect, useState, useCallback } from "react";
import { View } from "react-native";
import { useLazyQuery, useMutation } from "@apollo/client";

import { useSelector } from "react-redux";
import { Redirect } from "react-router-dom";
import { RootState } from "store/types";

import { Alert, Button, LoadingView, Text, styles } from "UpduoLibrary";
import {
  skaleRed,
  skaleWhite,
  skaleWebBackgroundGray,
  skaleWebTextGray,
  colorWithOpacity,
} from "config/UpduoColors";
import { getNodes, reorder } from "config/Utilities";
import PlaceholderView from "./PlaceholderView";
import SkillListView from "./SkillListView";
import EditPublishedSkillModal from "../CurriculumDetailRoute/EditPublishedSkillModal";
import { GET_CURRICULUM_LIST } from "graphql/internal/Queries";
import { EDIT_SKILL, EDIT_SKILL_MODULE } from "graphql/internal/Mutations";
import * as UpduoAnalytics from "config/UpduoAnalytics";
import { GetCurriculumListQuery } from "graphql/internal/generated";

export default function CurriculumRoute({ history }) {
  const selfObject = useSelector((state: RootState) => state.selfObject);
  const authToken = useSelector((state: RootState) => state.authToken);

  const [isLoading, setIsLoading] = useState(true);
  const [skillModulesList, setSkillModulesList] = useState<any[]>([]);
  const [editAlert, setEditAlert] = useState("");

  const [editPublishedSkillModalVisible, setEditPublishedSkillModalVisible] =
    useState(false);

  // Hack for "library" organizations:
  // Only Content page for non-Maintainance users, plus Members page and editing only for Maintenance users:
  const libraryOrganizationIds =
    process.env.REACT_APP_LIBRARY_ORGANIZATION_IDS?.split(",") || [];
  const isLibraryOrganization = libraryOrganizationIds.includes(
    selfObject?.organizationId
  );
  const canEdit =
    (isLibraryOrganization && selfObject.storeName === "Maintenance") ||
    !isLibraryOrganization;

  const [skillModuleIndexDelete, setSkillModuleIndexDelete] = useState();
  const [skillIndexDelete, setSkillIndexDelete] = useState();

  const [getCurriculumList, { loading: getCurriculumListLoading }] =
    useLazyQuery<GetCurriculumListQuery>(GET_CURRICULUM_LIST, {
      fetchPolicy: "network-only",
      onError: (error) => {
        console.error("ERROR getCurriculumList", error);
        UpduoAnalytics.logEvent("app-curriculum-getcurriculumlist-error", {
          error,
        });
      },
      onCompleted: ({ staffSkillModules: getCurriculumListData }) => {
        const formattedCurriculumList = getNodes(getCurriculumListData).map(
          (skillModule) => {
            return {
              ...skillModule,
              activeSkillList: getNodes(skillModule.activeSkillList),
            };
          }
        );
        setSkillModulesList(formattedCurriculumList);
        setIsLoading(false);
      },
    });

  const [editSkillModule, {}] = useMutation(EDIT_SKILL_MODULE, {
    fetchPolicy: "no-cache",
    onCompleted: ({ editSkillModule: editSkillModuleData }) => {
      if (!editSkillModuleData.ok) {
        console.error("ERROR editSkillModule", editSkillModuleData.reason);
        UpduoAnalytics.logEvent("app-curriculum-editskillmodule-error", {
          error: editSkillModuleData.reason,
        });
      }
      getCurriculumList();
    },
    onError: (error) => {
      console.error("ERROR editSkillModule", error);
      UpduoAnalytics.logEvent("app-curriculum-editskillmodule-error", {
        error,
      });
    },
  });

  const [editSkill] = useMutation(EDIT_SKILL, {
    fetchPolicy: "no-cache",
    onCompleted: ({ editSkill: editSkillData }) => {
      if (!editSkillData.ok) {
        console.error("ERROR editSkill", editSkillData.reason);
        UpduoAnalytics.logEvent("app-curriculum-editskill-error", {
          error: editSkillData.reason,
        });
      }
    },
    onError: (error) => {
      console.error("ERROR editSkill", error);
      UpduoAnalytics.logEvent("app-curriculum-editskill-error", {
        error,
      });
    },
  });

  useEffect(() => {
    getCurriculumList();
  }, [authToken]);

  useEffect(() => {
    // This effect hook handles checking and correcting ordering,
    // and does so for both the local state and remote database.
    // (Currently, this optimizes for minimizing number of API calls.)
    // This effect is guaranteed to trigger at least twice
    // (setSkillModulesList immediately following drag/drop, and then again after ordering),
    // basically requiring us to loop through the entire curriculum twice;
    // if local client performance during drag/drop becomes an issue, look here first.
    if (skillModulesList.length > 0) {
      const skillModuleSkillListsToFix: number[] = [];
      let needsSkillModuleOrderFix = false;
      let doesCurrentSkillListNeedFix;
      const newSkillModulesList = JSON.parse(JSON.stringify(skillModulesList));
      for (let k = 0; k < skillModulesList.length; k++) {
        doesCurrentSkillListNeedFix = false;
        needsSkillModuleOrderFix =
          needsSkillModuleOrderFix || skillModulesList[k].order !== k;
        newSkillModulesList[k].order = k;
        for (let m = 0; m < skillModulesList[k].activeSkillList.length; m++) {
          if (skillModulesList[k].activeSkillList[m].order !== m) {
            doesCurrentSkillListNeedFix = true;
            newSkillModulesList[k].activeSkillList[m].order = m;
          }
        }
        if (doesCurrentSkillListNeedFix) {
          skillModuleSkillListsToFix.push(k);
        }
      }
      if (skillModuleSkillListsToFix.length > 0 || needsSkillModuleOrderFix) {
        for (let n = 0; n < skillModuleSkillListsToFix.length; n++) {
          _handleFixSkillOrderInSkillModule(skillModuleSkillListsToFix[n]);
        }
        if (needsSkillModuleOrderFix) {
          _handleFixSkillModuleOrder();
        }
        setSkillModulesList(newSkillModulesList);
      }
    }
  }, [skillModulesList]);

  const _handleDeactivateSkill = useCallback(
    (skillModuleIndex, skillIndex, confirm = false) => {
      editSkill({
        variables: {
          editSkillRequest: {
            skillId:
              skillModulesList[skillModuleIndex].activeSkillList[skillIndex].id,
            isActive: false,
            flag: confirm ? "summit" : "",
          },
        },
      }).then((response) => {
        const message = response.data.editSkill.message;
        if (message) {
          setEditPublishedSkillModalVisible(true);
          // setIsLoading(false);
          setSkillModuleIndexDelete(skillModuleIndex);
          setSkillIndexDelete(skillIndex);
        }
        if (confirm) {
          setEditPublishedSkillModalVisible(false);
        }
        getCurriculumList();
      });
    },
    [skillModulesList, getCurriculumList]
  );

  const _handleDeactivateSkillModule = useCallback(
    (skillModuleIndex) => {
      const active_skill_count =
        skillModulesList[skillModuleIndex].activeSkillList.length;
      for (let skillIndex = 0; skillIndex < active_skill_count; skillIndex++) {
        _handleDeactivateSkill(skillModuleIndex, skillIndex);
      }
      editSkillModule({
        variables: {
          editSkillModuleRequest: {
            skillModuleId: skillModulesList[skillModuleIndex].id,
            isActive: false,
          },
        },
      });
    },
    [skillModulesList, _handleDeactivateSkill, getCurriculumList]
  );

  const _handleCloneSkill = (skillIndex) => {
    history.push(`/content/lesson/new/${skillIndex}`);
  };

  const _handleCloneSkillModule = (skillModuleIndex) => {
    history.push(`/content/topic/new/${skillModuleIndex}`);
  };

  const _handleFixSkillOrderInSkillModule = useCallback(
    (skillModuleIndex) => {
      const skillListToFix = skillModulesList[skillModuleIndex].activeSkillList;
      skillListToFix.forEach((item, index) => {
        if (item.order !== index) {
          editSkill({
            variables: {
              editSkillRequest: {
                skillId: item.id,
                order: index,
                skillModuleId: skillModulesList[skillModuleIndex].id,
              },
            },
          });
        }
      });
    },
    [skillModulesList]
  );

  const _handleFixSkillModuleOrder = useCallback(() => {
    const skillModulesListToFix = skillModulesList;
    skillModulesListToFix.forEach((item, index) => {
      if (item.order !== index) {
        editSkillModule({
          variables: {
            editSkillModuleRequest: {
              skillModuleId: item.id,
              order: index,
            },
          },
        });
      }
    });
  }, [skillModulesList]);

  const _handleDragEnd = useCallback(
    (result) => {
      if (!canEdit || !result.destination) {
        setEditAlert("Lesson editing is currently disabled.");
        return;
      }
      const oldSkillModulesList = JSON.parse(JSON.stringify(skillModulesList));
      if (result.type === "module") {
        // a skill module was moved
        const newSkillModulesList = reorder(
          oldSkillModulesList,
          result.source.index,
          result.destination.index
        );
        if (result.source.index !== result.destination.index) {
          setSkillModulesList(newSkillModulesList);
        }
      } else {
        // a skill was moved
        const startModuleIndex = parseInt(result.source.droppableId);
        const finishModuleIndex = parseInt(result.destination.droppableId);
        const start = oldSkillModulesList[startModuleIndex];
        const finish = oldSkillModulesList[finishModuleIndex];
        if (startModuleIndex === finishModuleIndex) {
          // skill stayed in the same module
          const skillModuleIndex = parseInt(result.source.droppableId);
          const activeSkillList = reorder(
            oldSkillModulesList[skillModuleIndex].activeSkillList,
            result.source.index,
            result.destination.index
          );
          const newSkillModulesList = JSON.parse(
            JSON.stringify(skillModulesList)
          );
          newSkillModulesList[skillModuleIndex].activeSkillList =
            activeSkillList;
          setSkillModulesList(newSkillModulesList);
        } else {
          // skill moved to a different module
          const movedSkill = start.activeSkillList[result.source.index];
          start.activeSkillList.splice(result.source.index, 1);
          finish.activeSkillList.splice(
            result.destination.index,
            0,
            movedSkill
          );
          // Fix edge case: moving 1st skill from one module to the 1st position of another module
          // Setting the moved skill's order to -1 (and thus not equal to its index 0) ensures that it gets moved
          if (result.source.index === 0 && result.destination.index === 0) {
            finish.activeSkillList[0].order = -1;
          }
          const newSkillModulesList = oldSkillModulesList;
          newSkillModulesList[startModuleIndex].activeSkillList =
            start.activeSkillList;
          newSkillModulesList[finishModuleIndex].activeSkillList =
            finish.activeSkillList;
          setSkillModulesList(newSkillModulesList);
        }
      }
    },
    [skillModulesList, canEdit]
  );

  if (!selfObject.isStaff) {
    return <Redirect to={"/"} />;
  }

  const userActionShield = (
    <View
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: window.innerHeight,
        backgroundColor: colorWithOpacity(skaleWhite, 0.7),
        zIndex: 15,
      }}
    >
      <LoadingView topSpacing={150} />
    </View>
  );
  return (
    <View style={{ flex: 1 }}>
      <View style={styles.subNavigationBar} />
      <View style={[styles.routeBodyContainer, { zIndex: 10 }]}>
        <View
          style={{
            zIndex: 5,
            justifyContent: "center",
          }}
        >
          <View
            style={{
              display: "flex",
              flex: 1,
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <View
              style={{
                width: 200,
                paddingVertical: 10,
                left: 20,
                alignSelf: "flex-end",
                justifyContent: "center",
              }}
            >
              <Text
                style={{
                  fontSize: 24,
                  fontWeight: "bold",
                  color: skaleWebTextGray,
                }}
              >
                {"Content"}
              </Text>
            </View>
            {canEdit ? (
              <View style={{ flexDirection: "row", marginRight: 20 }}>
                <Button
                  onPress={() => {
                    UpduoAnalytics.logEvent("user-module-new", {
                      isClone: false,
                    });
                    history.push("/content/topic/new");
                  }}
                  text={"+ New Topic"}
                />
                <Button
                  marginLeft={25}
                  onPress={() => {
                    if (skillModulesList.length > 0) {
                      UpduoAnalytics.logEvent("user-skill-new", {
                        isClone: false,
                      });
                      history.push("/content/lesson/new");
                    } else {
                      Alert(
                        "The curriculum does not contain any modules yet!",
                        "Please create a module first.",
                        [{ onPress: () => {} }]
                      );
                    }
                  }}
                  text={"+ New Lesson"}
                />
              </View>
            ) : null}
          </View>
        </View>
        {skillModulesList.length > 0 ? (
          <View
            style={{
              backgroundColor: skaleWhite,
              borderWidth: 1,
              borderColor: skaleWebBackgroundGray,
              marginHorizontal: 15,
              marginVertical: 28,
              borderRadius: 12,
              minWidth: 700,
            }}
          >
            <SkillListView
              canEdit={canEdit}
              _handleDragStart={() => {
                setEditAlert("");
              }}
              _handleDragEnd={_handleDragEnd}
              _handleDeactivateSkill={_handleDeactivateSkill}
              _handleDeactivateSkillModule={_handleDeactivateSkillModule}
              _handleCloneSkill={_handleCloneSkill}
              _handleCloneSkillModule={_handleCloneSkillModule}
              skillModulesList={skillModulesList}
            />
          </View>
        ) : (
          <PlaceholderView />
        )}
        <Text style={{ color: skaleRed }}>{editAlert}</Text>
        {isLoading || getCurriculumListLoading ? userActionShield : null}
      </View>
      <EditPublishedSkillModal
        onDismiss={() => setEditPublishedSkillModalVisible(false)}
        onEditPublishedSkillConfirm={() =>
          _handleDeactivateSkill(skillModuleIndexDelete, skillIndexDelete, true)
        }
        isVisible={editPublishedSkillModalVisible}
        isDelete={true}
      />
    </View>
  );
}
